import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';

interface ModerationItem {
  id: string;
  bodyText: string;
  visibility: string;
  isCommercial: string;
  createdAt: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  moderationDecision?: string;
  openaiResult?: any;
}

export default function AdminModeration() {
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ModerationItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  useEffect(() => {
    const loadQueue = async () => {
      try {
        const res = await api.admin.getModerationQueue();
        setItems(res);
      } catch (err) {
        console.error('Failed to load moderation queue:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadQueue();
  }, []);
  
  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      await api.admin.approveContent(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleReject = async (id: string, reason: string) => {
    setActionLoading(true);
    try {
      await api.admin.rejectContent(id, { reason });
      setItems((prev) => prev.filter((item) => item.id !== id));
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to reject:', err);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleWarnUser = async (userId: string, message: string) => {
    setActionLoading(true);
    try {
      await api.admin.warnUser(userId, { message });
      alert('Warning sent successfully');
    } catch (err) {
      console.error('Failed to warn user:', err);
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleBanUser = async (userId: string, reason: string) => {
    if (!confirm('Are you sure you want to ban this user?')) return;
    
    setActionLoading(true);
    try {
      await api.admin.banUser(userId, { reason });
      setItems((prev) => prev.filter((item) => item.authorId !== userId));
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to ban user:', err);
    } finally {
      setActionLoading(false);
    }
  };
  
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'hate':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'violence':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'sexual':
        return 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400';
      case 'harassment':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'spam':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
    }
  };
  
  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-2" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Moderation Queue</h1>
          <p className="text-slate-500 dark:text-slate-400">{items.length} items pending review</p>
        </div>
      </div>
      
      {items.length === 0 ? (
        <div className="card p-12 text-center">
          <CheckIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">All caught up!</h2>
          <p className="text-slate-500 dark:text-slate-400">No content pending moderation</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Items list */}
          <div className="space-y-3">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={`card p-4 w-full text-left transition-all ${
                  selectedItem?.id === item.id
                    ? 'ring-2 ring-primary-500'
                    : 'hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="avatar">
                    {(item.authorName || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900 dark:text-white">
                        {item.authorName}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{item.bodyText}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                        Pending Review
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Detail panel */}
          {selectedItem ? (
            <div className="card p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Content Details</h3>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Author info */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="avatar">
                    {(selectedItem.authorName || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedItem.authorName}
                    </p>
                    <p className="text-sm text-slate-500">{selectedItem.authorEmail}</p>
                  </div>
                </div>
                
                {/* Content */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Content</h4>
                  <p className="text-slate-900 dark:text-white p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    {selectedItem.bodyText}
                  </p>
                </div>
                
                {/* Metadata */}
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  <p>Created: {formatDateTime(new Date(selectedItem.createdAt))}</p>
                  <p>Visibility: {selectedItem.visibility}</p>
                  {selectedItem.openaiResult && (
                    <p className="text-xs mt-1">Moderation: {selectedItem.moderationDecision || 'pending'}</p>
                  )}
                </div>
                
                {/* Actions */}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleApprove(selectedItem.id)}
                      disabled={actionLoading}
                      className="btn bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Enter rejection reason:');
                        if (reason) handleReject(selectedItem.id, reason);
                      }}
                      disabled={actionLoading}
                      className="btn bg-red-600 hover:bg-red-700 text-white"
                    >
                      <XIcon className="w-4 h-4 mr-2" />
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        const message = prompt('Enter warning message:');
                        if (message) handleWarnUser(selectedItem.authorId, message);
                      }}
                      disabled={actionLoading}
                      className="btn btn-secondary"
                    >
                      Warn User
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Enter ban reason:');
                        if (reason) handleBanUser(selectedItem.authorId, reason);
                      }}
                      disabled={actionLoading}
                      className="btn bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400"
                    >
                      Ban User
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <p className="text-slate-500 dark:text-slate-400">
                Select an item to review
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
